import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import API from "../api/api";
import "./ManageSlider.css";

const ManageSlider = () => {
  const [images, setImages] = useState([]);
  const [file, setFile] = useState(null);
  const [link, setLink] = useState("");

  const [mainCategories, setMainCategories] = useState([]);
  const [categoriesMap, setCategoriesMap] = useState({});
  const [selectedMain, setSelectedMain] = useState("");
  const [filteredCategories, setFilteredCategories] = useState([]);

  // Load categories & main categories
  const loadCategories = async () => {
    try {
      const res = await API.get("/schemes");

      const mains = [];
      const map = {};

      res.data.forEach((scheme) => {
        mains.push(scheme.mainCategory);
        map[scheme.mainCategory] = scheme.categories.map((c) => c.name);
      });

      setMainCategories([...new Set(mains)]);
      setCategoriesMap(map);
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };

  // Load slider images
  const loadImages = async () => {
    try {
      const res = await API.get("/slider");
      const sorted = res.data.sort((a, b) => (a.order || 0) - (b.order || 0));
      setImages(sorted);
    } catch (err) {
      console.error("Error loading images:", err);
      Swal.fire("Error", "Failed to load images", "error");
    }
  };

  useEffect(() => {
    loadImages();
    loadCategories();
  }, []);

  // VALIDATE IMAGE DIMENSIONS (2400×600)
  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const img = new Image();
    img.src = URL.createObjectURL(selected);

    img.onload = () => {
      if (img.width !== 2400 || img.height !== 600) {
        Swal.fire({
          icon: "error",
          title: "Invalid Image Size",
          text: "Image must be exactly 2400 × 600 pixels.",
        });
        e.target.value = ""; // reset
        setFile(null);
        return;
      }
      setFile(selected); // valid
    };
  };

  // Upload image
  const uploadImage = async (e) => {
    e.preventDefault();
    if (!file) return;

    const confirmed = await Swal.fire({
      title: "Upload Image?",
      text: "This will add a new image to your slider.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, upload",
    });

    if (!confirmed.isConfirmed) return;

    const formData = new FormData();
    formData.append("image", file);
    if (link) formData.append("link", link);

    try {
      await API.post("/slider", formData);

      setFile(null);
      setLink("");
      setSelectedMain("");
      setFilteredCategories([]);

      Swal.fire({
        icon: "success",
        title: "Uploaded!",
        timer: 1500,
        showConfirmButton: false,
      });

      loadImages();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to upload image", "error");
    }
  };

  // Delete image
  const deleteImg = async (id) => {
    const confirmed = await Swal.fire({
      title: "Delete Image?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });

    if (!confirmed.isConfirmed) return;

    try {
      await API.delete(`/slider/${id}`);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        timer: 1200,
        showConfirmButton: false,
      });

      loadImages();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Delete failed", "error");
    }
  };

  // Move image up/down
  const moveImage = (index, direction) => {
    const updated = [...images];

    if (direction === "up" && index > 0) {
      [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];
    }
    if (direction === "down" && index < updated.length - 1) {
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    }

    const reordered = updated.map((img, i) => ({
      ...img,
      order: i + 1,
    }));

    setImages(reordered);
  };

  const saveOrder = async () => {
    const confirmed = await Swal.fire({
      title: "Save Slider Order?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Save",
    });

    if (!confirmed.isConfirmed) return;

    try {
      await API.put("/slider/reorder", { images });

      Swal.fire({
        icon: "success",
        title: "Order Saved!",
        timer: 1500,
        showConfirmButton: false,
      });

      loadImages();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to save order", "error");
    }
  };

  return (
    <div className="slider-wrapper">
      <div className="slider-container shadow-lg">
        <h3 className="slider-title">Manage Slider Images</h3>
        <div className="underline"></div>

        {/* Upload Form */}
        <form onSubmit={uploadImage} className="upload-box">

          {/* File Upload */}
          <input
            type="file"
            className="form-control upload-input"
            onChange={handleFileSelect}
            required
          />

          {/* Dimension Message */}
          <small className="text-danger">
            Recommended size: <strong>2400 × 600 pixels</strong>
          </small>

          {/* Custom Link */}
          <input
            type="text"
            className="form-control mt-2"
            placeholder="Custom Link (optional)"
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />

          {/* Main Category Selection */}
          <select
            className="form-select mt-2"
            value={selectedMain}
            onChange={(e) => {
              const main = e.target.value;
              setSelectedMain(main);
              setFilteredCategories(categoriesMap[main] || []);
              setLink("");
            }}
          >
            <option value="">Select Service Group (optional)</option>
            {mainCategories.map((mc, i) => (
              <option key={i} value={mc}>
                {mc}
              </option>
            ))}
          </select>

          {/* Category Dropdown */}
          {selectedMain && (
            <select
              className="form-select mt-2"
              onChange={(e) => {
                const cat = e.target.value;
                setLink(`/category/${encodeURIComponent(cat)}`);
              }}
            >
              <option value="">Select Service Type (optional)</option>
              {filteredCategories.map((cat, i) => (
                <option key={i} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          )}

          <button type="submit" className="btn upload-btn mt-3">
            Upload Image
          </button>
        </form>

        {/* Slider Images Display */}
        <div className="slider-grid mt-4">
          {images.length === 0 ? (
            <p>No images uploaded.</p>
          ) : (
            images.map((img, index) => (
              <div className="slider-grid-item" key={img._id}>
                <div className="image-card">
                  <img src={img.imageUrl} className="slider-img" alt="Slider" />

                  {img.link && (
                    <div className="text-center mt-1">
                      <small className="text-primary">
                        Link: <u>{img.link}</u>
                      </small>
                    </div>
                  )}

                  <div className="image-controls">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => moveImage(index, "up")}
                      disabled={index === 0}
                    >
                      ↑
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary mx-2"
                      onClick={() => moveImage(index, "down")}
                      disabled={index === images.length - 1}
                    >
                      ↓
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteImg(img._id)}
                    >
                      ✖
                    </button>
                  </div>

                  <div className="text-center mt-2">
                    <small>Position: {index + 1}</small>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {images.length > 1 && (
          <div className="text-center mt-4">
            <button className="btn save-btn" onClick={saveOrder}>
              💾 Save Order
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageSlider;
